import React, {
  MouseEvent as ReactMouseEvent,
  useEffect,
  useState,
} from 'react';
import { Skeleton } from '@mui/material';

import {
  ClickableWrapper,
  ContentComponent,
  DateTypography,
  ImageComponent,
  StyledBox,
  StyledCard,
  SummaryAndLinkContainer,
  TitleTypography,
} from './styles';
import { InfoCardProps } from './types';
import { TranslationKeys } from '../../../../i18n/locales/defaultResources';
import { useI18n } from '../../../../hooks/useI18n';
import ActionButton from '../../../components/ActionButton/ActionButton';
import {
  formatDate,
  mapLocaleAccordingToNzRules,
} from '../../../../helpers/localeFormatter';

const NewsCard: React.FC<InfoCardProps> = ({
  countryIsoCode,
  title,
  description,
  pubDate,
  imageUrl,
  onReadMore,
  isImageLoaded,
  onImageLoad,
  onSkeletonComplete,
}) => {
  const { t } = useI18n();
  const [loaded, setLoaded] = useState<boolean>(isImageLoaded ?? false);
  const mappedCountry = mapLocaleAccordingToNzRules(countryIsoCode);

  useEffect(() => {
    if (imageUrl && !isImageLoaded) {
      const img = new Image();
      img.src = imageUrl;
      img.onload = () => {
        if (onImageLoad) {
          onImageLoad();
          setLoaded(true);
          onSkeletonComplete && onSkeletonComplete();
        }
      };
    }
  }, [imageUrl, isImageLoaded, onImageLoad, onSkeletonComplete]);

  const handleCardClick = (event: ReactMouseEvent<HTMLDivElement>) => {
    onReadMore(event.nativeEvent);
  };

  const handleReadMoreClick = () => {
    onReadMore(new MouseEvent('click'));
  };

  return (
    <ClickableWrapper onClick={handleCardClick}>
      <StyledCard>
        <StyledBox>
          <ImageComponent isImageLoaded={loaded}>
            {imageUrl && !loaded ? (
              <Skeleton
                variant="rectangular"
                width={400}
                height={264}
                animation="wave"
              />
            ) : (
              imageUrl && <img src={imageUrl} alt={title} className="fade-in" />
            )}
          </ImageComponent>
          <ContentComponent>
            <DateTypography>
              {`${formatDate(pubDate, mappedCountry)}`}
            </DateTypography>
            <TitleTypography>{title}</TitleTypography>
            <SummaryAndLinkContainer>
              <div
                className="description"
                dangerouslySetInnerHTML={{ __html: description ?? '' }}
              />
              <div className="read-more-container">
                <ActionButton
                  variant="link-button"
                  buttonText={t(TranslationKeys.infocard_readmore)}
                  onClickAction={handleReadMoreClick}
                />
              </div>
            </SummaryAndLinkContainer>
          </ContentComponent>
        </StyledBox>
      </StyledCard>
    </ClickableWrapper>
  );
};

export default NewsCard;
